import { useEffect, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void }) => string;
      remove: (id: string) => void;
    };
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/**
 * In dev/test, VITE_TURNSTILE_SITE_KEY is Cloudflare's test key, so
 * the widget immediately hands back a passing token. So the form isn't locked even if the script fails to load,
 * a dummy token is passed through (the server is the final judge).
 */
export function Turnstile({ onToken }: { onToken: (token: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  useEffect(() => {
    if (!siteKey || !ref.current) {
      onToken("unavailable");
      return;
    }
    const el = ref.current;
    let widgetId: string | undefined;

    const render = () => {
      if (!window.turnstile) return;
      widgetId = window.turnstile.render(el, { sitekey: siteKey, callback: onToken });
    };

    if (window.turnstile) {
      render();
    } else {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = render;
      script.onerror = () => onToken("unavailable");
      document.head.appendChild(script);
    }

    return () => {
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [siteKey, onToken]);

  return <div ref={ref} className="mb-4" />;
}
